import { useEffect, useState, type ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../api";
import { useToast } from "../components/Toast";
import type { Diagnostics as Diag } from "../../shared/types";

export default function Diagnostics() {
  const [data, setData] = useState<Diag | null>(null);
  const toast = useToast();

  async function reload() {
    setData(await api.getDiagnostics());
  }

  useEffect(() => {
    reload().catch((err) => toast.show("error", err.message));
  }, []);

  async function deleteOrphanEntry(id: string) {
    if (!confirm(`Delete entry ${id}?`)) return;
    try {
      await api.deleteEntry(id);
      toast.show("success", `Entry ${id} deleted`);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  async function deleteOrphanImage(type: Diag["orphanImages"][number]["type"], filename: string) {
    if (!confirm(`Delete image ${filename}?`)) return;
    try {
      await api.deleteImage(type, filename);
      toast.show("success", `Image ${filename} deleted`);
      await reload();
    } catch (err) {
      toast.show("error", (err as Error).message);
    }
  }

  if (!data) return <div className="text-gh-muted">Loading…</div>;

  return (
    <div className="space-y-6">
      <Section
        title="Untranslated entries"
        description="У записі для однієї чи кількох неосновних мов відсутня назва або столиця. Відкрий запис і заповни порожні поля."
      >
        {data.untranslated.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm">
            {data.untranslated.map((u) => (
              <li key={u.id} className="px-3 py-1 border-b border-gh-border last:border-b-0">
                <span className="font-mono text-xs mr-3">{u.id}</span>
                <span className="text-gh-muted text-xs">missing: {u.missing.join(", ")}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Orphans (entries not in any category)"
        description="Запис існує, але жодна категорія на нього не посилається — у гру він не потрапить. Додай у категорію або видали, якщо не потрібен."
      >
        {data.orphans.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm">
            {data.orphans.map((id) => (
              <li
                key={id}
                className="px-3 py-1 border-b border-gh-border last:border-b-0 flex items-center"
              >
                <span className="font-mono text-xs flex-1">{id}</span>
                <button
                  onClick={() => deleteOrphanEntry(id)}
                  className="text-gh-muted hover:text-gh-danger"
                  aria-label={`delete ${id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Broken category refs"
        description="Категорія посилається на id, якого вже немає серед записів. Прибери id з категорії або відновлюй запис."
      >
        {data.brokenRefs.length === 0 ? (
          <Empty />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gh-muted">
              <tr>
                <th className="px-3 py-1">cat_id</th>
                <th className="px-3 py-1">id</th>
                <th className="px-3 py-1">reason</th>
              </tr>
            </thead>
            <tbody>
              {data.brokenRefs.map((b, i) => (
                <tr key={i} className="border-t border-gh-border">
                  <td className="px-3 py-1">{b.cat_id}</td>
                  <td className="px-3 py-1 font-mono text-xs">{b.id}</td>
                  <td className="px-3 py-1 text-gh-muted">{b.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Missing images"
        description="Запис посилається на файл прапора/мапи/герба, якого немає на диску. Завантаж картинку повторно на сторінці запису."
      >
        {data.missingImages.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm">
            {data.missingImages.map((m, i) => (
              <li key={i} className="px-3 py-1 border-b border-gh-border last:border-b-0">
                <span className="font-mono text-xs mr-3">{m.id}</span>
                <span className="text-xs text-gh-muted">
                  {m.type}: {m.path}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Orphan images"
        description="Файли картинок лежать на диску, але жоден запис на них не посилається. Можна сміливо видаляти, щоб звільнити місце."
      >
        {data.orphanImages.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm">
            {data.orphanImages.map((o, i) => (
              <li
                key={i}
                className="px-3 py-1 border-b border-gh-border last:border-b-0 flex items-center"
              >
                <span className="text-xs text-gh-muted mr-3">{o.type}</span>
                <span className="font-mono text-xs flex-1">{o.filename}</span>
                <button
                  onClick={() => deleteOrphanImage(o.type, o.filename)}
                  className="text-gh-muted hover:text-gh-danger"
                  aria-label={`delete ${o.filename}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-gh-surface border border-gh-border rounded">
      <div className="px-4 py-2 border-b border-gh-border">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-0.5 text-xs text-gh-muted">{description}</p>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Empty() {
  return <p className="px-4 py-3 text-xs text-gh-muted">All clean.</p>;
}
